import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Zap } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/layout/PageHero";
import { useLang } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";

const sectors = ["Government", "Defence", "Space", "Healthcare", "Energy", "Finance", "Industry", "Research", "Other"];
const deployments = ["On-Premise", "Sovereign Cloud", "Private Cloud", "Hybrid", "Undecided"];

const inputCls =
  "w-full rounded-xl border border-navy-border bg-navy px-4 py-3 text-sm text-white placeholder:text-silver/40 focus:border-astro-blue focus:outline-none focus:ring-2 focus:ring-astro-blue/40 transition-colors";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-2 block text-sm font-medium text-silver/80">
        {label} {required && <span className="text-astro-light">*</span>}
      </span>
      {children}
    </label>
  );
}

export default function PrivateBeta() {
  const { tr } = useLang();
  const [form, setForm] = useState({
    name: "", email: "", organization: "", role: "", sector: "", country: "", deployment: "", use_case: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/beta", form);
      setDone(true);
      toast.success(tr("Request received. Our team will review and respond."));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || tr("Could not submit request."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title={tr("Request Private Beta — AI Governance OS")}
        description={tr("Request access to the AstroLife™ AI Governance OS private beta for governments, critical infrastructure and regulated industries.")}
        path="/private-beta"
        keywords="AI Governance OS private beta, request access, AI Governance Platform"
      />

      <PageHero
        overline={tr("Private Beta")}
        title={tr("Request access to the AI Governance OS private beta.")}
        subtitle={tr("A programme for governments, critical infrastructure and regulated industries. Early access, direct engagement with our engineering team, and a voice in the roadmap.")}
        breadcrumbs={[{ label: tr("Private Beta") }]}
      />

      <section className="bg-navy py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <ul className="space-y-5">
                {[
                  { icon: Zap, title: "Early access", text: "Be among the first to operate the AI Governance OS." },
                  { icon: ShieldCheck, title: "Sovereign deployment", text: "On-premise, sovereign-cloud or hybrid — your requirements." },
                  { icon: CheckCircle2, title: "Direct engagement", text: "Work directly with our engineering team." },
                ].map((b) => (
                  <li key={b.title} className="flex gap-4 rounded-2xl border border-navy-border bg-navy-surface p-6">
                    <b.icon className="h-6 w-6 flex-shrink-0 text-astro-light" />
                    <div>
                      <p className="font-heading font-semibold text-white">{tr(b.title)}</p>
                      <p className="mt-1 text-sm text-silver/60">{tr(b.text)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-8">
              {done ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center rounded-2xl border border-astro-blue/30 bg-navy-surface p-16 text-center"
                  data-testid="beta-success"
                >
                  <CheckCircle2 className="h-14 w-14 text-astro-light" />
                  <h2 className="mt-6 font-heading text-2xl font-bold text-white">{tr("Request received")}</h2>
                  <p className="mt-3 max-w-md text-silver/60">{tr("Thank you for your interest in the AstroLife AI Governance OS private beta. Our team will review your request and be in touch.")}</p>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="rounded-2xl border border-navy-border bg-navy-surface p-8 md:p-10" data-testid="beta-form">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label={tr("Full name")} required><input required value={form.name} onChange={set("name")} data-testid="beta-name" className={inputCls} /></Field>
                    <Field label={tr("Work email")} required><input required type="email" value={form.email} onChange={set("email")} data-testid="beta-email" className={inputCls} /></Field>
                    <Field label={tr("Organization")} required><input required value={form.organization} onChange={set("organization")} data-testid="beta-org" className={inputCls} /></Field>
                    <Field label={tr("Role")}><input value={form.role} onChange={set("role")} data-testid="beta-role" className={inputCls} /></Field>
                    <Field label={tr("Sector")}>
                      <select value={form.sector} onChange={set("sector")} data-testid="beta-sector" className={inputCls}>
                        <option value="">{tr("Select a sector")}</option>
                        {sectors.map((s) => <option key={s} value={s}>{tr(s)}</option>)}
                      </select>
                    </Field>
                    <Field label={tr("Country")}><input value={form.country} onChange={set("country")} data-testid="beta-country" className={inputCls} /></Field>
                    <Field label={tr("Preferred deployment")} className="sm:col-span-2">
                      <select value={form.deployment} onChange={set("deployment")} data-testid="beta-deployment" className={inputCls}>
                        <option value="">{tr("Select deployment model")}</option>
                        {deployments.map((d) => <option key={d} value={d}>{tr(d)}</option>)}
                      </select>
                    </Field>
                    <Field label={tr("Use case")} className="sm:col-span-2">
                      <textarea rows={4} value={form.use_case} onChange={set("use_case")} data-testid="beta-usecase" placeholder={tr("Briefly describe your AI governance objectives")} className={inputCls} />
                    </Field>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="beta-submit"
                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-astro-blue px-8 py-4 font-semibold text-white hover:bg-astro-light transition-colors disabled:opacity-60"
                  >
                    {submitting ? tr("Submitting…") : tr("Request Private Beta")}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
